import { useState } from 'react';
import { missionApi } from '../../api/mission.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import LeafletMapPicker from '../../components/map/LeafletMapPicker';
import LeafletRouteMap from '../../components/map/LeafletRouteMap';

// Dev/test utility: pick two points, ask the backend (get_shortest_path ->
// GraphHopperAPI.calculateShortestPath) for the shortest driving route
// between them, and render it. Useful for checking the GraphHopper server
// (localhost:8003) is actually reachable and returning sane routes without
// having to go through a real mission/ticket flow.
export default function MapTestPage() {
  const { addToast } = useUI();
  const [origin, setOrigin] = useState({ lat: '', lng: '' });
  const [destination, setDestination] = useState({ lat: '', lng: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const bothPicked = origin.lat && origin.lng && destination.lat && destination.lng;

  const calculate = async () => {
    if (!bothPicked) { addToast('هر دو نقطه شروع و پایان را روی نقشه انتخاب کنید', 'error'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await missionApi.getShortestPath({
        org_lat: origin.lat, org_lng: origin.lng,
        dest_lat: destination.lat, dest_lng: destination.lng,
      });
      const { distance, lat, lng } = res.data || {};
      if (!Array.isArray(lat) || lat.length === 0) {
        addToast('مسیری یافت نشد - سرور مسیریابی (GraphHopper) را بررسی کنید', 'error');
      } else {
        setResult({ distance, lat, lng });
      }
    } catch {
      addToast('خطا در محاسبه مسیر - سرور مسیریابی در دسترس نیست', 'error');
    }
    setLoading(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="تست نقشه و مسیریابی" subtitle="دو نقطه را انتخاب کنید تا کوتاه‌ترین مسیر و فاصله بین آن‌ها محاسبه شود" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>📍 نقطه شروع</h3>
          <LeafletMapPicker lat={origin.lat} lng={origin.lng} onChange={(lat, lng) => setOrigin({ lat, lng })} height={280} />
          {origin.lat && origin.lng && (
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 8, direction: 'ltr', textAlign: 'left' }}>
              {Number(origin.lat).toFixed(5)}, {Number(origin.lng).toFixed(5)}
            </p>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>🏁 نقطه پایان</h3>
          <LeafletMapPicker lat={destination.lat} lng={destination.lng} onChange={(lat, lng) => setDestination({ lat, lng })} height={280} />
          {destination.lat && destination.lng && (
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 8, direction: 'ltr', textAlign: 'left' }}>
              {Number(destination.lat).toFixed(5)}, {Number(destination.lng).toFixed(5)}
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <Btn onClick={calculate} loading={loading} disabled={!bothPicked}>
          🧭 محاسبه کوتاه‌ترین مسیر
        </Btn>
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><Spinner /></div>}

      {result && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>مسیر محاسبه‌شده</h3>
            {typeof result.distance === 'number' && (
              <span style={{ fontSize: 13, fontWeight: 700, color: '#233A7A' }}>
                فاصله: {toPersian(result.distance.toFixed(2))} کیلومتر
              </span>
            )}
          </div>
          <LeafletRouteMap
            lat={result.lat}
            lng={result.lng}
            origin={{ lat: Number(origin.lat), lng: Number(origin.lng) }}
            destination={{ lat: Number(destination.lat), lng: Number(destination.lng) }}
            // LeafletRouteMap's own distance label divides by 1000 (expects
            // meters) - get_shortest_path returns kilometers directly, so
            // convert back up to keep that label correct; the accurate
            // value is already shown above regardless.
            distance={result.distance * 1000}
            height={420}
          />
        </div>
      )}
    </div>
  );
}
